import { DataTableSearch } from '@/components/ui/table/data-table-search';
import { useProjectTableFilters } from './use-case-table-filters';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { downloadCSV } from '@/lib/download-file';
import { toast } from 'sonner';
import { memo } from 'react';
import type { ProjectListItem } from '@/services/types';

type ProjectTableActionProps = {
  data: ProjectListItem[];
};

function ProjectTableAction({ data }: ProjectTableActionProps) {
  const { searchQuery, setSearchQuery, sort, setSort, resetFilters, isAnyFilterActive } = useProjectTableFilters();

  const sortingOptions = [
    { label: 'Most Recent', value: 'desc' },
    { label: 'Least Recent', value: 'asc' }
  ];

  const handleSortingChange = (value: string) => {
    setSort(value);
  };

  const handleDownload = async () => {
    try {
      const toISO = (d?: Date) => (d ? new Date(d).toISOString() : '');
      
      const mapped = (data || []).map((p: any) => ({
        title: p?.title || '',
        summary: p?.summary || '',
        timeline: p?.timeline || '',
        status: p?.status || '',
        tags: p?.tags?.join(' | ') || '',
        organisation: p?.organisation?.name || '',
        collaboratingOrganisations: p?.collaboratingOrganisations?.join(' | ') || '',
        media_name: p?.media?.name || '',
        media_url: p?.media?.url || '',
        continent: p?.continent || '',
        country: p?.country || '',
        region: p?.region || '',
        state: p?.state || '',
        keyOutcomes: p?.keyOutcomes || '',
        createdAt: toISO(p?.createdAt),
        updatedAt: toISO(p?.updatedAt)
      }));

      downloadCSV(mapped, 'projects');
    } catch (error) {
      toast.error('Failed to download projects');
      console.error('Download failed', error);
    }
  };

  return (
    <div className='flex flex-wrap items-center justify-between gap-4 px-5'>
      <DataTableSearch
        searchKey='projects'
        searchQuery={searchQuery || ''}
        setSearchQuery={setSearchQuery as any}
        setPage={async () => new URLSearchParams()} 
        className='bg-[#F7F7F7]'
      />

      <div className='flex items-center gap-4'>
        {/* Sorting Select */}
        <div className='flex items-center gap-2'>
          <p className='font-grotesk text-text-primary'>Sort By</p>
          <Select
            onValueChange={handleSortingChange}
            value={sort || ''}
          >
            <SelectTrigger className='w-[150px]'>
              <SelectValue placeholder='Select sorting' />
            </SelectTrigger>
            <SelectContent>
              {sortingOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Export Button */}
        <div className='flex items-center gap-2'>
          <Button
            variant={'outline'}
            onClick={handleDownload}
          >
            Export Projects
          </Button>
        </div>

        {/* Reset Filters Button */}
        {isAnyFilterActive && (
          <Button
            variant='outline'
            onClick={resetFilters}
          >
            Reset Filters
          </Button>
        )}
      </div>
    </div>
  );
}

export default memo(ProjectTableAction);